/**
 * <s-sticky-atc> — Sticky add to cart bar (blocks/sticky-add-to-cart.liquid).
 *
 * Shows the bar (data-visible) once the main quantity / add to cart row
 * ([data-sticky-atc-target] inside the same product form) has scrolled out of view
 * above the viewport, and hides it again when the buyer scrolls back up to it.
 *
 * Option dropdowns ([data-sticky-atc-option] = option name) mirror the main
 * <s-variant-picker>: a change selects the same value there and dispatches its
 * `change` event, so the picker runs its normal partial update.
 *
 * While visible, the bar's height (+ its bottom offset on larger screens) is written to
 * --sticky-atc-space on <html>, which the block's CSS uses as body padding so the
 * end of the page stays reachable.
 *
 * The element lives inside the 'product-form' partial, so a variant change replaces it.
 * The last visibility is kept at module level and restored instantly (data-instant)
 * on reconnect, so the bar does not slide away and back on every variant change.
 */
let lastVisible = false;

class SStickyAtc extends HTMLElement {
  #ac;
  #observer;
  #resizeObserver;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const form = this.closest('form');
    const target = form?.querySelector('[data-sticky-atc-target]');
    const bar = this.querySelector('[data-sticky-atc-bar]');
    if (!target || !bar || !('IntersectionObserver' in window)) return;

    this.addEventListener('change', (event) => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.dataset.stickyAtcOption) return;
      this.#selectInMainPicker(form, select.dataset.stickyAtcOption, select.value);
    }, opts);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateSpace(bar));
      this.#resizeObserver.observe(bar);
    }

    if (lastVisible) {
      this.setAttribute('data-instant', '');
      this.#setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.removeAttribute('data-instant'));
      });
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.#setVisible(scrolledPast);
    });
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#ac?.abort();
  }

  #setVisible(visible) {
    lastVisible = visible;
    this.toggleAttribute('data-visible', visible);
    this.querySelector('[data-sticky-atc-bar]')?.toggleAttribute('inert', !visible);
  }

  #updateSpace(bar) {
    const offset = Number.parseFloat(getComputedStyle(bar).bottom) || 0;
    const space = Math.ceil(bar.offsetHeight + offset);
    document.documentElement.style.setProperty('--sticky-atc-space', `${space}px`);
  }

  #selectInMainPicker(form, optionName, value) {
    const picker = form.querySelector('s-variant-picker');
    if (!picker) return;

    const controls = Array.from(picker.querySelectorAll('input[type="radio"], select'))
      .filter((control) => control.name === optionName);

    for (const control of controls) {
      if (control instanceof HTMLSelectElement) {
        if (control.value === value) return;
        control.value = value;
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (control.value === value) {
        if (control.checked) return;
        control.checked = true;
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }
}

if (!customElements.get('s-sticky-atc')) {
  customElements.define('s-sticky-atc', SStickyAtc);
}
